/**
 * Username Setup Provider
 * 
 * Wraps the app to provide username setup modal functionality.
 * Shows blocking modal when wallet connects without a username.
 */

import { createContext, useContext, ReactNode } from 'react';
import { useUsernameSetup } from '@/hooks/useUsernameSetup';
import { UsernameSetupModal } from './UsernameSetupModal';
import { toast } from 'sonner';

interface UsernameSetupContextValue {
  currentUsername: string | null;
  needsUsername: boolean;
  isLoading: boolean;
}

const UsernameSetupContext = createContext<UsernameSetupContextValue>({
  currentUsername: null,
  needsUsername: false,
  isLoading: true,
});

export function useUsernameContext() {
  return useContext(UsernameSetupContext);
}

interface UsernameSetupProviderProps {
  children: ReactNode;
}

export function UsernameSetupProvider({ children }: UsernameSetupProviderProps) {
  const {
    isLoading,
    needsUsername,
    currentUsername,
    showSetupModal,
    walletAddress,
    saveUsername,
  } = useUsernameSetup();

  const handleComplete = async (username: string): Promise<string | null> => {
    const result = await saveUsername(username);
    if (result.success) {
      toast.success(`You're now @${username}`);
      return null;
    } else {
      const error = result.error || 'Failed to set username';
      toast.error(error);
      return error;
    }
  };

  return (
    <UsernameSetupContext.Provider value={{ currentUsername, needsUsername, isLoading }}>
      {children}
      {showSetupModal && walletAddress && (
        <UsernameSetupModal
          open={showSetupModal}
          walletAddress={walletAddress}
          onComplete={handleComplete}
        />
      )}
    </UsernameSetupContext.Provider>
  );
}
